// "Come with me?" (features 10/16): the client side of the `come` function (supabase/functions/come). The person shares
// the link from their own phone (share sheet or copy); Magellan sends nothing. The links this person made are kept on
// this device (per person, lib/owner.ts clears them on a change of person) so the detail can say when a friend is in.
import { APP_URL, fn } from '../config'
import { callFn, ensureSession, supabase, type Item } from './api'
import { qaState } from './flags'

const KEY = 'mg_come_links'
const MAX_KEPT = 40
export type ComeLink = { token: string; token_id: string; item_id: string; at: number }
export type Opened = { item: Item & { hours_text?: string | null }; mine: boolean; in_count: number; token_id: string; expires_at: string }

export const comeUrl = (token: string) => `${APP_URL}/c/${token}`
/** The sample invite the QA panel opens (no server call, nothing saved). */
export const QA_SAMPLE_TOKEN = 'qa-sample'

function readLinks(): ComeLink[] { try { const v = JSON.parse(localStorage.getItem(KEY) ?? '[]'); return Array.isArray(v) ? v as ComeLink[] : [] } catch { return [] } }
function writeLinks(l: ComeLink[]) { try { localStorage.setItem(KEY, JSON.stringify(l.slice(-MAX_KEPT))) } catch { /* the link still works */ } }
/** Drop a link that was never sent (the share sheet was closed). */
export function forgetLink(token: string) { writeLinks(readLinks().filter((l) => l.token !== token)) }
export const linksFor = (itemId: string) => readLinks().filter((l) => l.item_id === itemId)

const qa = () => (qaState().on ? { qa: true } : {})

/** A new link for this item (one per tap; each is its own invite). */
export async function createLink(itemId: string): Promise<ComeLink> {
  const r = await callFn<{ token: string; token_id: string }>('come', { action: 'create', item_id: itemId, ...qa() })
  const link = { token: r.token, token_id: r.token_id, item_id: itemId, at: Date.now() }
  writeLinks([...readLinks(), link])
  return link
}

/** How many friends said yes to this person's links for one item. */
export async function inCount(itemId: string): Promise<number> {
  const links = linksFor(itemId)
  if (!links.length) return 0
  const r = await callFn<{ counts: Record<string, number> }>('come', { action: 'status', tokens: links.map((l) => l.token) })
  return Object.values(r.counts ?? {}).reduce((a, b) => a + b, 0)
}

export class ComeError extends Error {
  status: number | null
  constructor(status: number | null, message: string) { super(message); this.status = status }
}

/** Open a link. A signed-out visitor can see the pick; with a session the answer says whether it is their own link. */
export async function openLink(token: string): Promise<Opened> {
  // supabase-js sends the session when there is one, else the public key
  const { data, error } = await supabase.functions.invoke(fn('come'), { body: { action: 'open', token } })
  if (error) {
    let status: number | null = null
    try { status = (error as { context?: Response }).context?.status ?? null } catch { /* keep null */ }
    throw new ComeError(status, error.message)
  }
  return data as Opened
}

/** The friend says I'm in (a session is made first, like every visitor's). */
export async function sayIn(token: string): Promise<{ already: boolean }> {
  await ensureSession()
  return callFn<{ ok: boolean; already: boolean }>('come', { action: 'in', token, ...qa() })
}
